import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {SERVER_API_URL} from '../../app.constants';
import {createRequestOption} from '../../shared/util/request-util';
import {IFactor} from "../../shared/model/factor.model";
import {IFactorWorkflow, IFactorWorkflowListItem, IFactorWorkflowStepForm} from "../../shared/model/factor-workflow.model";
import {IFactorWorkflowStepConfig} from "../../shared/model/factor-workflow-step-config.model";
import {IProject} from "../../shared/model/project.model";


type EntityResponseType = HttpResponse<IFactor>;
type EntityArrayResponseType = HttpResponse<IFactor[]>;

@Injectable({providedIn: 'root'})
export class FactorService {
  public resourceUrl = SERVER_API_URL + 'api/factors';

  constructor(protected http: HttpClient) {
  }

  create(factor: IFactor): Observable<EntityResponseType> {
    return this.http.post<IFactor>(this.resourceUrl, factor, {observe: 'response'});
  }

  update(factor: IFactor): Observable<EntityResponseType> {
    return this.http.put<IFactor>(this.resourceUrl, factor, {observe: 'response'});
  }

  assignSupplier(factorId: number, payload: {
    supplierId: number;
    supplierAmount: number;
    assignDateStr: string;
  }): Observable<EntityResponseType> {
    return this.http.put<IFactor>(`${this.resourceUrl}/${factorId}/assign-supplier`, payload, {observe: 'response'});
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http.get<IFactor>(`${this.resourceUrl}/${id}`, {observe: 'response'});
  }

  findFactorCount(): Observable<HttpResponse<any>> {
    return this.http
      .get<any>(`${this.resourceUrl + '-all-count'}`, {observe: 'response'});
  }

  findFactorMonthly(param: any): Observable<HttpResponse<any>> {
    return this.http
      .get<IFactor>(`${this.resourceUrl + '-monthly/' + param}`, {observe: 'response'});
  }

  findFactorSumSales(param: any): Observable<HttpResponse<any>> {
    return this.http
      .get<any>(`${this.resourceUrl + '-sum-sales/' + param}`, {observe: 'response'});
  }

  findFactorSumSalesMainFactor(param: any): Observable<HttpResponse<any>> {
    return this.http
      .get<any>(`${this.resourceUrl + '-sum-sales-main-factor/' + param}`, {observe: 'response'});
  }

  findFactorSumProducts(param: any, req?: any): Observable<HttpResponse<any>> {
    const options = createRequestOption(req);

    return this.http
      .get<any>(`${this.resourceUrl + '-sum-products/' + param}`, {params: options, observe: 'response'});
  }

  findFactorSumProductsMainFactor(param: any, req?: any): Observable<HttpResponse<any>> {
    const options = createRequestOption(req);

    return this.http
      .get<any>(`${this.resourceUrl + '-sum-products-main-factor/' + param}`, {params: options, observe: 'response'});
  }

  findFactorSumSuppliers(param: any): Observable<HttpResponse<any>> {
    return this.http
      .get<any>(`${this.resourceUrl + '-sum-supplier/' + param}`, {observe: 'response'});
  }

  findFactorSumSuppliersMainFactor(param: any): Observable<HttpResponse<any>> {
    return this.http
      .get<any>(`${this.resourceUrl + '-sum-supplier-main-factor/' + param}`, {observe: 'response'});
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<IFactor[]>(this.resourceUrl, {params: options, observe: 'response'});
  }

  search(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<IFactor[]>(this.resourceUrl + '-search', {params: options, observe: 'response'});
  }

  findWithCustomerId(req?: any, id?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<IFactor[]>(this.resourceUrl + '-customer/' + id, {params: options, observe: 'response'});
  }

  findWithPrjId(req?: any, id?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<IFactor[]>(this.resourceUrl + '-project/' + id, {params: options, observe: 'response'});
  }

  findWithSupplierId(req?: any, id?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<IFactor[]>(this.resourceUrl + '-supplier/' + id, {params: options, observe: 'response'});
  }

  // findByStatus(req?: any, status?:any): Observable<EntityArrayResponseType> {
  //   const options = createRequestOption(req);
  //   return this.http.get<IFactor[]>(this.resourceUrl + '-list-status/'+status, {params: options, observe: 'response'});
  // }

  findByStatus(req?: any, status?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<IFactor[]>(this.resourceUrl + '-list-status/' + status, {
      params: options,
      observe: 'response'
    });
  }


  chekFOrArchive(id?: any): Observable<EntityArrayResponseType> {
    // const options = createRequestOption(req);
    return this.http.get<IFactor[]>(this.resourceUrl + '-check-archive/' + id, {params: null, observe: 'response'});
  }

  getWorkflow(id: number): Observable<HttpResponse<IFactorWorkflow>> {
    return this.http.get<IFactorWorkflow>(`${this.resourceUrl}/${id}/workflow`, {observe: 'response'});
  }

  listWorkflowsByProject(projectId: number): Observable<HttpResponse<IFactorWorkflowListItem[]>> {
    return this.http.get<IFactorWorkflowListItem[]>(`${SERVER_API_URL}api/projects/${projectId}/factor-workflows`, {observe: 'response'});
  }

  createWorkflowRequest(projectId: number, form?: IFactorWorkflowStepForm): Observable<HttpResponse<IFactorWorkflow>> {
    return this.http.post<IFactorWorkflow>(`${SERVER_API_URL}api/projects/${projectId}/factor-workflows`, form || {}, {observe: 'response'});
  }

  saveWorkflowStepForm(id: number, form: IFactorWorkflowStepForm): Observable<HttpResponse<IFactorWorkflow>> {
    return this.http.put<IFactorWorkflow>(`${this.resourceUrl}/${id}/workflow/step-form`, form, {observe: 'response'});
  }

  saveAndAdvanceWorkflow(id: number, form: IFactorWorkflowStepForm): Observable<HttpResponse<IFactorWorkflow>> {
    return this.http.post<IFactorWorkflow>(`${this.resourceUrl}/${id}/workflow/save-and-advance`, form, {observe: 'response'});
  }

  advanceWorkflow(id: number): Observable<HttpResponse<IFactorWorkflow>> {
    return this.http.post<IFactorWorkflow>(`${this.resourceUrl}/${id}/workflow/advance`, {}, {observe: 'response'});
  }

  approveProduction(id: number): Observable<HttpResponse<IFactorWorkflow>> {
    return this.http.post<IFactorWorkflow>(`${this.resourceUrl}/${id}/workflow/approve-production`, {}, {observe: 'response'});
  }

  listAllWorkflows(req?: any): Observable<HttpResponse<IFactorWorkflowListItem[]>> {
    const options = createRequestOption(req);
    return this.http.get<IFactorWorkflowListItem[]>(`${SERVER_API_URL}api/factor-workflows`, {params: options, observe: 'response'});
  }

  listMyWorkflowTasks(req?: any): Observable<HttpResponse<IFactorWorkflowListItem[]>> {
    const options = createRequestOption(req);
    return this.http.get<IFactorWorkflowListItem[]>(`${SERVER_API_URL}api/factor-workflows/my-tasks`, {params: options, observe: 'response'});
  }

  listWorkflowStepConfigs(): Observable<HttpResponse<IFactorWorkflowStepConfig[]>> {
    return this.http.get<IFactorWorkflowStepConfig[]>(`${SERVER_API_URL}api/factor-workflow-step-configs`, {observe: 'response'});
  }

  saveWorkflowStepConfigs(items: IFactorWorkflowStepConfig[]): Observable<HttpResponse<IFactorWorkflowStepConfig[]>> {
    return this.http.put<IFactorWorkflowStepConfig[]>(`${SERVER_API_URL}api/factor-workflow-step-configs`, items, {observe: 'response'});
  }

  delete(id: number): Observable<HttpResponse<any>> {
    return this.http.delete<any>(`${this.resourceUrl}/${id}`, {observe: 'response'});
  }


  querySendNotify(req?: any): Observable<HttpResponse<IFactor[]>> {
    const options = createRequestOption(req);

    return this.http.get<any>(this.resourceUrl + '-send-notify', {params: options, observe: 'response'});
  }

  getCountProductFactor(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-product-report-count', {params: options, observe: 'response'});
  }

  getCountProductFactorMainFactor(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-product-report-count-main-factor', {
      params: options,
      observe: 'response'
    });
  }

  getCountTotalProfit(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-total-profit-count', {params: options, observe: 'response'});
  }

  getCountTotalProfitMainFactor(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-total-profit-count-main-factor', {
      params: options,
      observe: 'response'
    });
  }

  getCountTotalProfitPeriod(param: any): Observable<HttpResponse<any>> {
    return this.http
      .get<any>(`${this.resourceUrl + '-total-profit-count-period/' + param}`, {observe: 'response'});
  }

  getCountTotalProfitPeriodMainFactor(param: any): Observable<HttpResponse<any>> {
    return this.http
      .get<any>(`${this.resourceUrl + '-total-profit-count-period-main-factor/' + param}`, {observe: 'response'});
  }

  getCountFactorAmountForProjectWithSupplier(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-project-supplier-count', {params: options, observe: 'response'});
  }

  getCountFactorAmountTotalWithSupplier(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-total-supplier-count', {params: options, observe: 'response'});
  }

  getCountFactorAmountTotalWithSupplierMainFactor(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-total-supplier-count-main-factor', {
      params: options,
      observe: 'response'
    });
  }

  getCountFactorAmountForProject(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-project-count', {params: options, observe: 'response'});
  }

  getCountFactorAmountForSupplier(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-supplier-count', {params: options, observe: 'response'});
  }

  getCountFactorAmountTotal(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-total-count', {params: options, observe: 'response'});
  }

  getCountFactorAmountTotalMainFactor(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-total-count-main-factor', {params: options, observe: 'response'});
  }

  getCountProfitAmountForProject(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-project-profit-count', {params: options, observe: 'response'});
  }

  searchReportCount(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-search-report-count', {params: options, observe: 'response'});
  }

  searchReportCountSupplier(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any>(this.resourceUrl + '-search-report-count-supplier', {
      params: options,
      observe: 'response'
    });
  }

}
